"use client";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/navigation";
import { PlusCircleOutlined, ArrowRightOutlined } from "@ant-design/icons";
import type { Blog } from "@/lib/@types/api/blog";
import { getBlogList } from "@/lib/api/blog";
import moment from "moment/moment";
import { Button, Empty, Spin } from "antd";
import { useTranslations } from "next-intl";
import { replaceImgUrl } from "@/lib/utils.ts";

const IndustryNews = ({ locale }) => {
	const t = useTranslations();
	const [blogList, setBlogList] = useState<Blog.BlogListItem[]>([]);
	const [pageSize, setPageSize] = useState(1);
	const [searchLoading, setSearchLoading] = useState(false);
	const limit = 12;

	const fetchBlogList = async (
		Params: Blog.GetBlogListParams,
	): Promise<{ blogList: Blog.BlogListItem[]; blogCount: number }> => {
		setSearchLoading(true);
		try {
			const res = await getBlogList(Params);
			setSearchLoading(false);
			return { blogList: res.detail.blog_list, blogCount: res.detail.blog_filter_count };
		} catch (error) {
			setSearchLoading(false);
			console.error("Error fetching blog list:", error);
			return { blogList: [], blogCount: 0 }; // 如果发生错误，返回一个空数组
		}
	};

	useEffect(() => {
		fetchBlogList({
			lang_code: { lang_code: locale },
			page: pageSize,
			limit,
			blog_title: "",
		}).then((res) => {
			if (res) {
				setBlogList(res.blogList);
			}
		});
	}, []);

	return (
		<div className="bg-[#f5f5f5]">
			<div className="container py-16 max-md:py-8">
				<div className="flex flex-col gap-[12px]">
					<div className="text-center text-[3rem] font-bold leading-normal text-[#212121] max-md:text-2xl">
						{t("resource.Industry News")}
					</div>
					<div className="flex flex-row items-center justify-end gap-[12px] max-md:flex-col">
						<div className="flex justify-end max-md:w-full">
							<Link
								className="flex cursor-pointer justify-end gap-[20px] text-[#212121] hover:text-[#0e6aed] max-md:gap-[8px]"
								href="/blog"
							>
								<span className="whitespace-nowrap text-[20px] max-md:text-[14px]">
									{t("resource.View More")}
								</span>
								<PlusCircleOutlined className="text-[26px] max-md:text-[18px]" />
							</Link>
						</div>
					</div>
				</div>
				{searchLoading ? (
					<Spin size="large" />
				) : blogList?.length > 0 ? (
					<div className="mt-8 flex cursor-pointer flex-row gap-[20px] max-md:flex-col">
						{blogList?.slice(0, 1).map((item: any, index: number) => {
							{
								return (
									<Link
										href={"/blog/" + item.blog_slug}
										className="industry-news-item relative flex h-[450px] flex-[3] overflow-hidden"
										key={index}
									>
										<div className="absolute bottom-0 left-0 right-0 top-0">
											<img
												className="industry-news-item-img h-full w-full object-cover duration-700"
												src={`${replaceImgUrl(item.blog_cover_origin)}`}
											/>
										</div>
										<div className="relative flex h-full w-full flex-col justify-between">
											<div className="flex flex-col items-end justify-end px-[30px] py-[42px] text-white max-md:px-[16px] max-md:py-[16px]">
												<div className="text-[12px] font-bold">
													{moment(item.blog_upload_time).format("MMM YYYY")}
												</div>
												<div className="text-[35px] font-bold max-md:text-[20px]">
													{moment(item.blog_upload_time).format("DD")}
												</div>
											</div>
											<div
												className="px-[30px] py-[42px] text-[25px] font-bold text-white max-md:px-[16px] max-md:py-[16px] max-md:text-[16px]"
												style={{ background: "linear-gradient(to bottom, transparent, rgba(0,0,0,.4))" }}
											>
												{item.blog_title}
											</div>
										</div>
									</Link>
								);
							}
						})}
						{blogList?.slice(1, 3).map((item: any, index: number) => {
							{
								return (
									<Link
										href={"/blog/" + item.blog_slug}
										className="industry-news-item relative flex h-[450px] flex-[2] cursor-pointer flex-col overflow-hidden"
										key={index}
									>
										<div className="overflow-hidden">
											<img
												className="industry-news-item-img h-[180px] w-full object-cover duration-700 max-md:h-[160px]"
												src={`${replaceImgUrl(item.blog_cover_origin)}`}
											/>
										</div>
										<div className="flex-1 bg-[#212121] px-[24px] py-[22px] text-white">
											<div>
												<div className="border-b pb-4 text-[20px] font-bold leading-[32px] max-md:text-[16px]">
													{item.blog_title}
												</div>
												<p className="my-2 line-clamp-2 pt-2 text-[15px] font-normal max-md:my-1 max-md:text-[12px] sm:text-sm">
													{item.blog_excerpt}
												</p>
											</div>
											<div className="mt-4 flex items-center justify-between">
												<div>
													<div className="text-[12px] font-bold">
														{moment(item.blog_upload_time).format("MMM YYYY")}
													</div>
													<div className="text-[35px] font-bold max-md:text-[20px]">
														{moment(item.blog_upload_time).format("DD")}
													</div>
												</div>
												<ArrowRightOutlined className="industry-news-item-gobtn transform text-[24px] transition-all duration-700 group-hover:translate-x-2" />
											</div>
										</div>
									</Link>
								);
							}
						})}
					</div>
				) : (
					<Empty />
				)}
			</div>
		</div>
	);
};

export default IndustryNews;
